import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { useEnduserStore } from "../store/hooks";
import type { FormResponseCategory } from "../../shared/schema";

export interface UseEnduserFormResponsesOptions {
  limit?: number;
  /** Filter by form response category */
  category?: FormResponseCategory;
  /** Form IDs to hide (typically from organization settings) */
  hiddenFormIds?: string[];
  /** Whether to enable the query (defaults to true) */
  enabled?: boolean;
}

/**
 * Hook for fetching and filtering form responses for the current enduser.
 *
 * Filtering logic:
 * - Not manually hidden from enduser portal
 * - Not an unsubmitted internal note
 * - One of:
 *   - Submitted by this enduser, OR
 *   - Outstanding (needs completion - no submittedBy and no draftSavedAt), OR
 *   - Has fields shared with enduser
 * - Form not in organization's hidden list
 *
 * Category filtering:
 * - "submitted": Only form responses with submittedAt
 * - "unsubmitted": Only form responses without submittedAt
 * - "all": All visible form responses
 */
export function useEnduserFormResponses(options: UseEnduserFormResponsesOptions = {}) {
  const { limit = 50, category = "all", hiddenFormIds = [], enabled = true } = options;
  const currentEnduser = useEnduserStore((state) => state.currentEnduser);

  const query = useTellescopeInfiniteQuery({
    model: "form_responses",
    method: "getSome",
    request: {
      filter: {},
      limit,
    },
    select: (data) => data.pages.flat(),
    enabled,
  });

  const allResponses = query.data ?? [];

  // Get current enduser ID for filtering
  const enduserId = currentEnduser?.id;

  // Apply visibility filtering logic
  const visibleResponses = allResponses.filter((f) => {
    // Not manually hidden from enduser portal
    if (f.hideFromEnduserPortal) return false;

    // Not an unsubmitted internal note
    if (!f.submittedAt && !!f.isInternalNote) return false;

    // Form not in organization's hidden list
    if (hiddenFormIds.includes(f.formId)) return false;

    // Must meet one of these visibility criteria:
    const submittedByEnduser = f.submittedBy === enduserId;
    const isOutstanding = !f.submittedBy && !f.draftSavedAt;
    const hasSharedFields = !!f.responses?.find((r) => r.sharedWithEnduser === true);

    return submittedByEnduser || isOutstanding || hasSharedFields;
  });

  // Apply category filtering
  const filteredResponses = visibleResponses.filter((f) => {
    switch (category) {
      case "submitted":
        return !!f.submittedAt;
      case "unsubmitted":
        return !f.submittedAt;
      case "all":
      default:
        return true;
    }
  });

  // Sort by most recent activity (submittedAt, draftSavedAt, or updatedAt)
  const sortedResponses = [...filteredResponses].sort((a, b) => {
    const aTime = a.submittedAt
      ? new Date(a.submittedAt).getTime()
      : a.draftSavedAt
      ? new Date(a.draftSavedAt).getTime()
      : new Date(a.updatedAt).getTime();
    const bTime = b.submittedAt
      ? new Date(b.submittedAt).getTime()
      : b.draftSavedAt
      ? new Date(b.draftSavedAt).getTime()
      : new Date(b.updatedAt).getTime();
    return bTime - aTime;
  });

  return {
    ...query,
    formResponses: sortedResponses,
  };
}
